import { Box, Button, Chip, Container, Divider, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import logo from '../../../assets/logo.png';

const formatDate = (value) => {
  if (!value) return 'Not specified';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const renderPolicyContent = (content) => {
  const lines = String(content || '')
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map((line) => line.trim());
  let inList = false;

  return lines.map((line, index) => {
    if (!line) return <Box key={`space-${index}`} sx={{ height: 10 }} />;

    if (/^\d+\.\s+\S/.test(line)) {
      inList = false;
      return (
        <Typography
          key={`section-${index}`}
          component="h2"
          sx={{ mt: index ? 4 : 0, mb: 1.5, color: '#17315c', fontFamily: 'inherit', fontSize: { xs: 19, md: 22 }, fontWeight: 800, lineHeight: 1.35 }}
        >
          {line}
        </Typography>
      );
    }

    const subsection = line.match(/^(\d+\.\d+\s+[^.]+\.)\s*(.*)$/);
    if (subsection) {
      inList = false;
      return (
        <Typography key={`subsection-${index}`} component="p" sx={{ mt: 2, mb: 0, color: '#334155', fontFamily: 'inherit', fontSize: 15, lineHeight: 1.85 }}>
          <Box component="strong" sx={{ color: '#1f3b67', fontWeight: 750 }}>{subsection[1]}</Box>
          {subsection[2] ? ` ${subsection[2]}` : ''}
        </Typography>
      );
    }

    if (/^\d+\.\d+\s+\S/.test(line)) {
      inList = false;
      return (
        <Typography key={`subheading-${index}`} component="h3" sx={{ mt: 2.5, mb: .75, color: '#1f3b67', fontFamily: 'inherit', fontSize: 16, fontWeight: 750, lineHeight: 1.5 }}>
          {line}
        </Typography>
      );
    }

    if (inList) {
      if (line.endsWith('.')) inList = false;
      return (
        <Box key={`list-${index}`} sx={{ mt: 1, pl: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box sx={{ mt: '9px', width: 6, height: 6, borderRadius: '50%', flex: '0 0 auto', bgcolor: '#1976d2' }} />
          <Typography component="p" sx={{ m: 0, color: '#334155', fontFamily: 'inherit', fontSize: 15, lineHeight: 1.75 }}>
            {line.replace(/[;.]$/, '')}
          </Typography>
        </Box>
      );
    }

    if (line.endsWith(':')) inList = true;

    const isContactLine = /^(Cleany Corp\.|Email:|Subject line suggestion:|\d+\s+Madison Avenue)/i.test(line);
    return (
      <Typography
        key={`paragraph-${index}`}
        component="p"
        sx={{ mt: isContactLine ? .5 : 1.5, mb: 0, color: '#334155', fontFamily: 'inherit', fontSize: 15, fontWeight: isContactLine ? 650 : 400, lineHeight: 1.85 }}
      >
        {line}
      </Typography>
    );
  });
};

const LegalPolicyDocument = ({ policy, preview = false, onBack }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: '#f5f8fc', py: { xs: 2, md: 5 } }}>
    <Container maxWidth="md">
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        {onBack ? (
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }}>
            Back to editor
          </Button>
        ) : <Box />}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {preview && <Chip label="Draft preview" color="warning" size="small" />}
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => window.print()}
            sx={{ textTransform: 'none', bgcolor: '#fff' }}
          >
            Print
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #dfe8f3', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 3, md: 5 }, background: 'linear-gradient(120deg, #17315c, #1976d2)', color: '#fff' }}>
          <Box component="img" src={logo} alt="Cleany" sx={{ width: 150, mb: 3, filter: 'brightness(0) invert(1)' }} />
          <Typography component="h1" sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 800, lineHeight: 1.2 }}>
            {policy?.title || 'Legal Policy'}
          </Typography>
          <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,.8)', fontSize: 14 }}>
            Effective date: {formatDate(policy?.effective_date)}
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Box
            sx={{
              overflowWrap: 'anywhere',
              fontFamily: "Georgia, 'Times New Roman', serif",
            }}
          >
            {renderPolicyContent(policy?.content || 'No policy content is available.')}
          </Box>
          <Divider sx={{ my: 4 }} />
          <Typography sx={{ color: '#64748b', fontSize: 12 }}>
            Last updated: {policy?.last_updated ? new Date(policy.last_updated).toLocaleString() : 'Not available'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  </Box>
);

export default LegalPolicyDocument;
